import { useState } from 'react';

import { IconPound } from '@iwoca/icons';
import { Button, Modal } from '@iwoca/orion';
import { useFormContext } from 'react-hook-form';

import { formatCurrency } from '../../../../components/Input/formatCurrency';
import { HookFormInput } from '../../../../components/Input/Input';
import { iwocapayTelDisplay } from '../../../../constants.json';
import { useUpdateTurnover } from '../../hooks/useUpdateTurnover';
import { FormValues } from '../../utils/PayLinkLanding.types';

export const UpdateTurnoverModal = ({
  isOpen,
  onClose,
  submitForm,
}: {
  isOpen: boolean;
  onClose: () => void;
  submitForm: ({
    values,
    acceptedSuperseed,
    updatedTurnover,
  }: {
    values: FormValues;
    acceptedSuperseed?: boolean;
    updatedTurnover?: boolean;
  }) => Promise<void>;
}) => {
  const { getValues, watch } = useFormContext<FormValues>();
  const { updateTurnover } = useUpdateTurnover();
  const [updatingTurnover, setUpdatingTurnover] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const turnover = watch('turnover');

  const onSubmit = async () => {
    // We are using local state here because react-hook-form's `formState.isSubmitting` cannot cope
    // with the fact that updateTurnover causes state-change (as its a mutation)
    setUpdatingTurnover(true);

    try {
      const values = getValues();
      const sanitizedTurnover = values.turnover.replace(/,/g, '');
      await updateTurnover(Number(sanitizedTurnover));
      await submitForm({
        values,
        updatedTurnover: true,
      });
    } catch {
      setSubmitError(true);
    } finally {
      setUpdatingTurnover(false);
    }
  };

  const handleOnClose = () => {
    // Only allow closing of the modal if turnover is not being updated
    if (!updatingTurnover) {
      onClose();
    }
  };

  return (
    // Prevent closing the modal while updating turnover
    <Modal isOpen={isOpen} onClose={handleOnClose}>
      <div className="mb-s text-2xl font-bold">
        It’s been a while - we need to double check we have the right
        information.{' '}
      </div>
      <div className="mb-l text-m font-reg">
        We’ll use it to update our records and refresh your spending limit.
      </div>
      <div className="text-m font-med">
        Your turnover for the last 12 months
      </div>
      <div className="mb-m text-s font-reg text-structure-40">
        This doesn't have to be exact to the penny
      </div>
      <HookFormInput
        className="mb-l"
        iconLeft={<IconPound />}
        name="turnover"
        formatter={formatCurrency}
        placeholder="Enter amount..."
      />
      <Button
        onClick={onSubmit}
        className="sm:w-full"
        loading={updatingTurnover}
        disabled={!turnover}
      >
        Update and continue
      </Button>
      {submitError && (
        <div className="pt-s text-[#af0d04] *:font-bold">
          Oops something went wrong. Please try again or give us a call on{' '}
          {iwocapayTelDisplay}.
        </div>
      )}
    </Modal>
  );
};
